import React, { ChangeEvent } from 'react'
import { FormikErrors, useFormik } from 'formik'
import i18next from 'i18next'

import { Text, Toast } from 'components'
import { TextInput, RightPanel, DatePicker } from 'components'

import { ApiKey, UpdateApiKeyPayload } from 'services/types/api-key'
import { useMutations } from 'queries/external-api'
import { formatDatetime } from 'utils/datetime'
import useUserPermissions from 'hooks/use-user-permissions'

import { validationSchema } from './helper'
import ApiRuleForm, { FormValues as RuleFormValues } from './ApiRuleForm'

type Props = {
  data?: ApiKey
  onClose: (refresh?: boolean) => void
}

export type FormValues = Omit<UpdateApiKeyPayload, 'api_key_id' | 'expiry_ts' | 'rules'> & {
  api_key_id?: string
  expiry_ts: Date | null
  rules: Array<RuleFormValues>
}

const initialRuleItem: RuleFormValues = {
  campaign_id: '',
  campaign: 'None',
  lead_source_id: '',
  lead_source: '',
  user_id: '',
  team_id: '',
  owner: '',
  lead_process_id: '',
  lead_process: '',
  is_default: false,
}

const ApiKeyForm: React.FC<Props> = ({ data, onClose }) => {
  const [{ canCreate, canEdit }] = useUserPermissions('externalApi')

  const isCreate = !data?.api_key_id

  const { create, update } = useMutations()

  const onSubmit = async (v: FormValues) => {
    if (isCreate) {
      const { isSuccess } = await create.mutateAsync({
        ...v,
        expiry_ts: formatDatetime(v.expiry_ts, 'yyyy-MM-dd') ?? null,
        rules: v.rules.map((i) => ({
          campaign_id: i.campaign_id ?? '',
          lead_source_id: i.lead_source_id ?? '',
          lead_process_id: i.lead_process_id ?? '',
          user_id: i.user_id,
          team_id: i.team_id,
          is_default: i.is_default,
        })),
      })
      isSuccess && onClose(isSuccess)
      return
    }

    if (!v.api_key_id) {
      Toast.toastMessage({
        message: i18next.t('validation_required', { name: 'api_key_id' }),
        variant: 'error',
      })
      return
    }

    const { isSuccess } = await update.mutateAsync({
      ...v,
      api_key_id: v.api_key_id,
      remarks: v.remarks,
      expiry_ts: formatDatetime(v.expiry_ts, 'yyyy-MM-dd') ?? null,
      rules: v.rules.map((i) => ({
        rule_id: i.rule_id,
        campaign_id: i.campaign_id ?? '',
        lead_source_id: i.lead_source_id ?? '',
        lead_process_id: i.lead_process_id ?? '',
        user_id: i.user_id,
        team_id: i.team_id,
        is_default: i.is_default,
      })),
    })

    isSuccess && onClose(isSuccess)
  }

  const initialValues: FormValues = isCreate
    ? {
        remarks: '',
        expiry_ts: null,
        rules: [{ ...initialRuleItem, is_default: true }],
      }
    : {
        api_key_id: data?.api_key_id,
        remarks: data?.remarks ?? '',
        expiry_ts: data?.expiration_ts ?? null,
        rules:
          data?.rules?.map((i) => ({
            rule_id: i.rule_id,
            campaign_id: i.campaign_id,
            campaign: i.campaign.campaign_name,
            lead_source_id: i.lead_source_id,
            lead_source: i.lead_source.lead_source_name,
            user_id: i.lead_owner.user_id,
            team_id: i.lead_owner.team_id,
            owner: i.lead_owner.team_name || i.lead_owner.fname + ' ' + i.lead_owner.lname,
            lead_process_id: i.lead_process_id,
            lead_process: i.lead_process,
            is_default: i.is_default,
          })) ?? [],
      }

  const { dirty, errors, isSubmitting, setFieldValue, submitForm, touched, values } = useFormik<FormValues>({
    initialValues,
    onSubmit,
    validationSchema,
  })

  const isReadOnly = isCreate ? !canCreate : !canEdit

  return (
    <RightPanel
      disableSubmit={!dirty}
      isSubmitting={isSubmitting}
      onCancel={onClose}
      onSubmit={submitForm}
      withSubmitConfirmation={!isCreate}
      title={isCreate ? 'new_api_key_title' : 'api_key_details_title'}
      width={600}
    >
      <TextInput
        label="remarks_lbl"
        onChange={(e: ChangeEvent<HTMLInputElement>) => setFieldValue('remarks', e.target.value)}
        value={values.remarks ?? undefined}
        variant="labeled"
        type="textarea"
        mb="1rem"
        readOnly={isReadOnly}
      />
      <DatePicker
        dateFormat="dd MMM yyyy"
        defaultToday={false}
        disablePast
        label="expiry_date_lbl"
        onChange={(v) => setFieldValue('expiry_ts', v)}
        value={values.expiry_ts || undefined}
        variant="labeled"
        readOnly={isReadOnly}
      />

      <>
        {values.rules.map((rule, idx) => (
          <ApiRuleForm
            key={idx}
            values={rule}
            onChange={(v) => {
              const updatedRules = [...values.rules]
              updatedRules[idx] = v
              setFieldValue('rules', updatedRules)
            }}
            errors={
              typeof errors.rules?.[idx] === 'object'
                ? (errors.rules?.[idx] as FormikErrors<RuleFormValues>)
                : undefined
            }
            touched={touched.rules?.[idx]}
            isDisabled={isReadOnly || isSubmitting}
            canDelete={values.rules.length > 1}
            onDelete={() => {
              const updatedRules = [...values.rules]
              updatedRules.splice(idx, 1)
              setFieldValue('rules', updatedRules)
            }}
            onDefaultClick={(checked) => {
              const updatedRules = [...values.rules]
              updatedRules.forEach((r, i) => {
                if (i === idx) {
                  r.is_default = checked
                } else {
                  r.is_default = checked ? false : r.is_default
                }
              })
              setFieldValue('rules', updatedRules)
            }}
          />
        ))}
      </>
      {/* TODO: enable this when support multiple rules
      <ActionButton
        mt="1rem"
        variant="add"
        onClick={() => {
          setFieldValue('rules', [...values.rules, initialRuleItem])
        }}
      >
        rule
      </ActionButton> */}
      {touched.rules && errors.rules && typeof errors.rules === 'string' && <Text variant="error">{errors.rules}</Text>}
    </RightPanel>
  )
}

export default ApiKeyForm
